import { Link } from "react-router-dom";
import { Check, ArrowRight, ChevronRight, X, Monitor, ShoppingCart, Server } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { usePageMeta } from "@/hooks/usePageMeta";
import ClientsCarousel from "@/components/ClientsCarousel";
import { JsonLd } from "@/components/JsonLd";

const planPresentation = {
  name: "Site de Prezentare",
  price: "250 lei",
  period: "+ TVA / lună",
  description: "Pentru site-uri WordPress de prezentare, business-uri locale și companii care au nevoie de un site actualizat, sigur și gestionat corect, fără complexitatea operațională a unui magazin online.",
  includes: [
    "Actualizări WordPress, pluginuri și temă gestionate proactiv",
    "Monitorizare uptime",
    "Verificări malware zilnice",
    "Backup-uri zilnice",
    "Raport lunar",
    "Verificări funcționale de bază",
    "Suport prin email / formular",
    "Restaurare din backup în caz de incident",
    "Modificări minore de conținut incluse, în limite rezonabile",
  ],
  modifications: "1 task mic de conținut pe lună, precum actualizare text, înlocuire imagine sau ajustări minore de prezentare.",
  whyCost: "Acest plan este potrivit pentru site-uri cu risc operațional mai redus, dar care au nevoie de mentenanță reală. Include monitorizare, actualizări gestionate atent, backup-uri, verificări de securitate și suport de bază. Este o investiție mică raportată la riscul unui site neîngrijit.",
};

const planWoo = {
  name: "WooCommerce",
  price: "de la 550 lei",
  period: "+ TVA / lună",
  description: "Pentru magazine online unde orice problemă poate afecta direct comenzile, checkout-ul și încrederea clienților. Acest plan include mai multă atenție operațională și verificări suplimentare pentru zonele critice.",
  includes: [
    "Tot ce este inclus în planul Site de Prezentare",
    "Verificări funcționale pentru magazin",
    "Verificarea checkout-ului",
    "Verificarea fluxului de comandă",
    "Suport prioritar",
    "Backup fișiere zilnic",
    "Backup bază de date de 4 ori pe zi",
    "Istoric backup de 60 de zile",
    "Monitorizare atentă a componentelor critice",
    "Verificări suplimentare după actualizări",
    "Recomandări de performanță și stabilitate",
  ],
  whyCost: "WooCommerce nu poate fi tratat la fel ca un site simplu de prezentare. Există mai multe componente critice, mai multe zone sensibile și impact direct în vânzări. Orice eroare poate însemna comenzi pierdute, clienți frustrați sau probleme operaționale. De aceea acest plan include backup-uri mai dese, verificări suplimentare și prioritate crescută la incidente.",
  whatMeans: "Prețul final depinde de trafic, numărul de pluginuri critice, complexitatea magazinului, integrări și cerințele de infrastructură. Pentru magazinele mai simple, prețul pornește de la 550 lei + TVA / lună.",
};

const planCustom = {
  name: "Custom / High Traffic",
  price: "Ofertă personalizată",
  description: "Pentru proiecte cu trafic mare, infrastructură specială, integrări multiple, multilingv, membership, sincronizări ERP/CRM sau cerințe avansate de disponibilitate și suport.",
  includes: [
    "Magazine cu trafic mare",
    "Site-uri cu multe integrări",
    "Proiecte cu mai multe medii sau fluxuri operaționale sensibile",
    "Site-uri care au nevoie de infrastructură dedicată",
    "Proiecte unde downtime-ul înseamnă pierderi semnificative",
  ],
};

const notIncluded = [
  "Redesign complet de site",
  "Dezvoltare custom complexă",
  "Funcționalități noi ample",
  "Campanii de marketing",
  "Creare completă de pagini noi complexe",
  "Refaceri structurale mari",
];

const extraServices = [
  "Optimizare avansată de viteză",
  "Migrare hosting",
  "Malware cleanup",
  "Landing pages noi",
  "Intervenții suplimentare",
  "Redesign punctual",
  "Consultanță pentru infrastructură",
  "Audit tehnic extins",
];

const anchors = [
  { id: "plan-prezentare", label: "Site de Prezentare", icon: Monitor, price: "250 lei / lună" },
  { id: "plan-woocommerce", label: "WooCommerce", icon: ShoppingCart, price: "de la 550 lei / lună" },
  { id: "plan-custom", label: "Custom / High Traffic", icon: Server, price: "Ofertă personalizată" },
];

export default function PackagesPage() {
  usePageMeta(
    "Pachete Mentenanță WordPress - Ordell WebCare",
    "Pachete clare de mentenanță WordPress: Site de Prezentare (250 lei/lună), WooCommerce (de la 550 lei/lună) și Custom. Fără costuri ascunse."
  );

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <JsonLd page="pachete" />
      {/* Hero */}
      <section data-testid="packages-hero" className="py-16 lg:py-20">
        <div className="section-container text-center max-w-3xl mx-auto">
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-bold text-[#1A1D20] mb-6">
            Pachete clare pentru site-uri WordPress și magazine WooCommerce
          </h1>
          <p className="text-base sm:text-lg text-[#4A5568] leading-relaxed mb-8">
            Fără grile complicate, fără jargon și fără costuri ascunse. Alegi varianta potrivită pentru nivelul de criticitate al site-ului tău.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto" data-testid="packages-nav-buttons">
            {anchors.map((a) => (
              <button
                key={a.id}
                onClick={() => scrollTo(a.id)}
                data-testid={`nav-${a.id}`}
                className="group bg-white text-left p-5 rounded-2xl border border-[#0F2942]/10 shadow-[0_4px_20px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_32px_rgb(0,0,0,0.1)] hover:-translate-y-1 transition-all"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-[#F0F5F4] flex items-center justify-center group-hover:bg-[#0F2942] transition-colors">
                    <a.icon className="w-4 h-4 text-[#1A4A40] group-hover:text-white transition-colors" />
                  </div>
                  <span className="font-['Outfit'] font-semibold text-[#1A1D20] text-sm">{a.label}</span>
                </div>
                <span className="text-xs text-[#64748B] pl-12">{a.price}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Plan 1: Prezentare */}
      <section id="plan-prezentare" data-testid="plan-presentation" className="py-12 lg:py-16 scroll-mt-20">
        <div className="section-container max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 lg:p-12 border border-[#0F2942]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-[#1A1D20] mb-2">{planPresentation.name}</h2>
            <div className="mb-4">
              <span className="font-['Outfit'] text-4xl font-bold text-[#0F2942]">{planPresentation.price}</span>
              <span className="text-[#4A5568] ml-2">{planPresentation.period}</span>
            </div>
            <p className="text-[#4A5568] leading-relaxed mb-8 max-w-2xl">{planPresentation.description}</p>

            <h3 className="font-['Outfit'] font-semibold text-[#1A1D20] mb-4">Include:</h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {planPresentation.includes.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-[#1A4A40] mt-0.5 shrink-0" />
                  <span className="text-sm text-[#1A1D20]">{item}</span>
                </div>
              ))}
            </div>

            <div className="bg-[#F0F5F4]/50 rounded-xl p-5 mb-6">
              <h4 className="font-['Outfit'] font-medium text-sm text-[#1A1D20] mb-2">Ce includ modificările minore</h4>
              <p className="text-sm text-[#4A5568]">{planPresentation.modifications}</p>
            </div>

            <div className="bg-[#FAFAFA] rounded-xl p-5 mb-8">
              <h4 className="font-['Outfit'] font-medium text-sm text-[#1A1D20] mb-2">De ce costă 250 lei + TVA</h4>
              <p className="text-sm text-[#4A5568] leading-relaxed">{planPresentation.whyCost}</p>
            </div>

            <Link
              to="/contact"
              data-testid="plan-presentation-cta"
              className="inline-flex items-center gap-2 bg-[#0F2942] text-white font-medium px-6 py-3 rounded-xl hover:bg-[#0F2942]/90 transition-all"
            >
              Vreau acest plan <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Plan 2: WooCommerce */}
      <section id="plan-woocommerce" data-testid="plan-woocommerce" className="py-12 lg:py-16 scroll-mt-20">
        <div className="section-container max-w-4xl mx-auto">
          <div className="bg-[#0A1118] text-white rounded-2xl p-8 lg:p-12 border border-[#133E59] shadow-lg relative overflow-hidden">
            <Badge className="bg-[#1A4A40] text-white border-none mb-4 text-[10px] tracking-wider uppercase font-semibold">
              Recomandat pentru magazine online
            </Badge>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold mb-2">{planWoo.name}</h2>
            <div className="mb-4">
              <span className="font-['Outfit'] text-4xl font-bold">{planWoo.price}</span>
              <span className="text-white/60 ml-2">{planWoo.period}</span>
            </div>
            <p className="text-white/70 leading-relaxed mb-8 max-w-2xl">{planWoo.description}</p>

            <h3 className="font-semibold mb-4">Include:</h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {planWoo.includes.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span className="text-sm text-white/80">{item}</span>
                </div>
              ))}
            </div>

            <div className="bg-white/5 rounded-xl p-5 mb-6 border border-white/10">
              <h4 className="font-medium text-sm mb-2">De ce costă de la 550 lei + TVA</h4>
              <p className="text-sm text-white/60 leading-relaxed">{planWoo.whyCost}</p>
            </div>

            <div className="bg-white/5 rounded-xl p-5 mb-8 border border-white/10">
              <h4 className="font-medium text-sm mb-2">Ce înseamnă „de la"</h4>
              <p className="text-sm text-white/60 leading-relaxed">{planWoo.whatMeans}</p>
            </div>

            <Link
              to="/contact"
              data-testid="plan-woo-cta"
              className="inline-flex items-center gap-2 bg-white text-[#0F2942] font-medium px-6 py-3 rounded-xl hover:bg-white/90 transition-all"
            >
              Cere ofertă WooCommerce <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Plan 3: Custom */}
      <section id="plan-custom" data-testid="plan-custom" className="py-12 lg:py-16 scroll-mt-20">
        <div className="section-container max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 lg:p-12 border border-[#0F2942]/10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-[#1A1D20] mb-2">{planCustom.name}</h2>
            <p className="font-['Outfit'] text-2xl font-bold text-[#0F2942] mb-4">{planCustom.price}</p>
            <p className="text-[#4A5568] leading-relaxed mb-8 max-w-2xl">{planCustom.description}</p>

            <h3 className="font-['Outfit'] font-semibold text-[#1A1D20] mb-4">Pot intra aici:</h3>
            <div className="space-y-3 mb-8">
              {planCustom.includes.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <ChevronRight className="w-4 h-4 text-[#1A4A40] mt-0.5 shrink-0" />
                  <span className="text-sm text-[#1A1D20]">{item}</span>
                </div>
              ))}
            </div>

            <Link
              to="/contact"
              data-testid="plan-custom-cta"
              className="inline-flex items-center gap-2 bg-[#0F2942] text-white font-medium px-6 py-3 rounded-xl hover:bg-[#0F2942]/90 transition-all"
            >
              Discută cu noi <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* NOT INCLUDED */}
      <section data-testid="not-included-section" className="py-16 lg:py-20 bg-[#F1F3F5]">
        <div className="section-container max-w-4xl mx-auto">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20] mb-4">
            Ce nu este inclus în abonamentele standard
          </h2>
          <p className="text-[#4A5568] leading-relaxed mb-8">
            Abonamentele de mentenanță nu includ implicit proiecte mari de redesign, dezvoltare custom complexă, funcționalități ample, copywriting complet, branding sau marketing.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {notIncluded.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3">
                <X className="w-4 h-4 text-red-400/70 mt-0.5 shrink-0" />
                <span className="text-sm text-[#4A5568]">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-[#64748B] italic">
            Acestea pot fi estimate separat, punctual sau în baza unei oferte personalizate.
          </p>
        </div>
      </section>

      {/* EXTRA SERVICES */}
      <section data-testid="extra-services-section" className="py-16 lg:py-20">
        <div className="section-container max-w-4xl mx-auto">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20] mb-8">
            Servicii suplimentare disponibile
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {extraServices.map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-[#0F2942]/10 text-sm text-[#1A1D20]">
                {item}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <ClientsCarousel />

      {/* CTA */}
      <section data-testid="packages-cta" className="py-20 lg:py-28 bg-[#0A1118] text-white">
        <div className="section-container text-center max-w-3xl mx-auto">
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-semibold mb-6">
            Nu plătești doar pentru mentenanță. Plătești pentru mai puține probleme și mai multă liniște.
          </h2>
          <Link
            to="/contact"
            data-testid="packages-final-cta"
            className="inline-flex items-center gap-2 bg-white text-[#0F2942] font-medium px-8 py-3.5 rounded-xl hover:bg-white/90 transition-all"
          >
            Cere audit gratuit <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
